import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';

interface EmailGateProps {
  open: boolean;
  onEmailSubmit: (email: string) => void;
}

const EmailGate: React.FC<EmailGateProps> = ({ open, onEmailSubmit }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  // Check if email exists in localStorage on mount
  useEffect(() => {
    const storedEmail = localStorage.getItem('userEmail');
    if (storedEmail) {
      onEmailSubmit(storedEmail);
    }
  }, [onEmailSubmit]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = () => {
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail) {
      setError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Store email in localStorage for persistence
    localStorage.setItem('userEmail', trimmedEmail);
    setError('');
    onEmailSubmit(trimmedEmail);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        // Prevent closing by clicking outside
        if (reason === 'backdropClick') {
          return;
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6">Sign In</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box py={1}>
          <Typography variant="body1" gutterBottom>
            Please enter your email address to access the FAR Market Research Pipeline.
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Access is restricted to authorized users only.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={error}
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={!email.trim()}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailGate;